import React, { createContext, useState, useContext, useMemo } from "react";

import { useQueries } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { Country } from "../../../../../../../models/country";
import { Port } from "../../../../../../../models/port";
import CountryService from "../../../../../../../services/country.service";
import PortService from "../../../../../../../services/port.service";
import { useAppState } from "../../../../../../../context/StateContext";

export type ExternalList = {
  countryList: Country[];
  portList: Port[];
};

interface OfficePageContextProps extends ExternalList {
  loading: boolean;
}

const OfficePageContext = createContext<OfficePageContextProps>({} as OfficePageContextProps);

interface OfficePageProviderProps {
  children: React.ReactChild;
}

type Request = Array<[setState: Function, execute: Function, ...executeArgs: any]>;

const OfficePageProvider = ({ children }: OfficePageProviderProps) => {
  const { currentUser } = useAppState();
  const companyId = currentUser?.companyId;

  const [countryList, setCountryList] = useState<Country[]>([]);
  const [portList, setPortList] = useState<Port[]>([]);

  const Resources: Request = useMemo(() => {
    return [
      [
        setCountryList,
        CountryService.getAll,
        {
          sortBy: JSON.stringify([["name", "ASC"]]),
        },
      ],
      [
        setPortList,
        PortService.getAll,
        {
          attributes: JSON.stringify(["id", "site", "siteCode"]),
          sortBy: JSON.stringify(["siteCode"]),
          include: JSON.stringify([
            { association: "companyPorts", required: true, where: { companyId } },
          ]),
        },
      ],
    ];
  }, []);

  const results = useQueries({
    queries: Resources.map(([setState, execute, args], index) => {
      return {
        queryKey: [`office-page-resources.${index}`],
        queryFn: () =>
          execute({
            ...args,
          }),
        refetchOnWindowFocus: false,
        enabled: !!companyId,
        onSuccess: (response: AxiosResponse<any>) => {
          if (response && response.data && response.data instanceof Array) {
            setState(response.data);
          }
        },
      };
    }),
  });

  const loadingQueries = results.some((result) => result.isFetching);

  return (
    <OfficePageContext.Provider
      value={{
        countryList,
        portList,
        loading: loadingQueries,
      }}
    >
      {children}
    </OfficePageContext.Provider>
  );
};

export { OfficePageContext, OfficePageProvider };

export const useOfficePageContext = () => {
  const context = useContext(OfficePageContext);

  return context;
};
